/**
 * Unberry support chat integration.
 *
 * Replaces Freshchat with the in-house <SupportChat> React widget
 * (@unberry/support-chat-widget), which talks to support-api.
 *
 * This module keeps the SAME exported functions the app already calls
 * (`initializeChatbot`, `updateChatbotData`) so the call sites in
 * views/LoadingScreen and views/GameLoader do not change. The difference from
 * the old placeholder is that the widget is a real React component rendered into
 * its own DOM node, not a `window.chatWidget` global.
 *
 * Auth: the widget is handed the candidate's existing assessment JWT
 * (storage.get.authToken()). support-api verifies it via RS256 against the
 * Unberry public key and reads the organizationId we pass through the widget —
 * candidate tokens do not carry one. See support-chat-widget/INTEGRATION.md.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { SupportChat } from '@unberry/support-chat-widget';
import '@unberry/support-chat-widget/styles.css';
import { storage } from 'services/config/storage';
import { setDocumentDetails } from 'utils/helper';
import { DEFAULT_COMPANY_NAME } from 'constants/globalConstants';
import { SUPPORT_CHAT_READY_EVENT } from 'constants/helperModalConstants';

const CONTAINER_ID = 'unberry-support-root';

/**
 * Resolves which support chat to show, honouring a DEV-ONLY override.
 *
 * Normally the chatbot type comes from the org config
 * (`organizationConfig.supportChatbotType`, defaulting to Freshchat). During
 * local development you can force the Unberry widget without changing any org
 * data by setting `REACT_APP_FORCE_UNBERRY_CHAT=true` in `.env.local`.
 *
 * The override is gated on both the env flag AND development mode, so it can
 * never take effect in a production build even if the flag leaks into an env
 * file. Use this from the LoadingScreen branches instead of reading
 * `supportChatbotType` directly.
 */
export function resolveChatbotType(orgConfigType) {
  const forced =
    process.env.REACT_APP_FORCE_UNBERRY_CHAT === 'true' &&
    process.env.NODE_ENV !== 'production';
  if (forced) return 'unberry-bot';
  return orgConfigType || 'freshchat';
}

// Module-level so initialize/update/teardown share one React root. The widget is
// a singleton per page, like Freshchat was.
let root = null;
let currentUser = null;
let currentMetadata = {};
let currentIsMobile = false;

/** Where the widget mounts. Created once, appended to <body>. */
function ensureContainer() {
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    document.body.appendChild(container);
  }
  return container;
}

/**
 * Builds the metadata attached to every message.
 *
 * The widget auto-detects browser / os / page / fullscreen / networkQuality, so
 * those are omitted here. This adds the assessment-specific context that makes
 * support useful: which org, position, link, and (via updateChatbotData) the
 * current game. Everything is read defensively — a missing value is simply left
 * out, never thrown.
 */
function buildMetadata(user, extra = {}) {
  const fcData = storage.get.fcData?.();
  return {
    organizationName: storage.get.orgInfo?.()?.name || undefined,
    positionId: storage.get.positionId?.() || undefined,
    linkId: storage.get.linkId?.() || undefined,
    designation: user?.designation || undefined,
    isMobile: currentIsMobile,
    tabSwitched: fcData ? storage.get.allTabSwitchCount?.() || 0 : 0,
    ...currentMetadata,
    ...extra,
  };
}

/** Reads the org from wherever it's available (orgId key, or orgInfo fallback). */
function readOrgId() {
  return (
    storage.get.orgId?.() ||
    storage.get.orgInfo?.()?.organizationId ||
    storage.get.orgInfo?.()?.id ||
    undefined
  );
}

// Retry handle so we don't stack multiple waiters.
let orgWaitTimer = null;

/**
 * (Re)renders the widget once BOTH the token and the org are available.
 *
 * The org id is written to storage with the game-sync response, which lands a
 * beat after login — so on first login render() runs before the org exists. We
 * wait for it (short polling, capped) and render when ready, so the widget never
 * mounts without an org and the socket connects cleanly the first time.
 */
function render() {
  const token = storage.get.authToken?.();
  if (!token) return; // not logged in yet; a later call will render

  const organizationId = readOrgId();

  // Org not here yet (first login, pre game-sync). Wait for it rather than
  // mounting a widget that can't authenticate. Re-check every 400ms, up to ~12s.
  if (!organizationId) {
    if (orgWaitTimer) return; // already waiting
    let waited = 0;
    orgWaitTimer = setInterval(() => {
      waited += 400;
      if (readOrgId() || waited >= 12000) {
        clearInterval(orgWaitTimer);
        orgWaitTimer = null;
        if (readOrgId()) render();
      }
    }, 400);
    return;
  }

  const container = ensureContainer();
  if (!root) root = createRoot(container);

  root.render(
    React.createElement(SupportChat, {
      apiUrl: process.env.REACT_APP_SUPPORT_API_URL,
      token,
      // Candidate tokens carry no org; the widget forwards this to support-api
      // via the X-Organization-Id header and the socket handshake.
      //
      // getOrganizationId is a LIVE reader, not a snapshot: the org id is written
      // to storage with the game-sync response, which lands a tick AFTER the
      // widget mounts. A static org would be empty at mount and every request
      // would omit the header → the backend rejects the candidate ("session
      // expired"). Reading live means the org is picked up as soon as it exists.
      // Falls back to orgInfo, which is set earlier (on ORG_SET_EVENT).
      getOrganizationId: readOrgId,
      organizationId,
      source: 'ASSESSMENT',
      metadata: buildMetadata(currentUser),
      theme: {
        primaryColor: '#6366f1',
        title: 'Unberry Support Agent',
        subtitle: 'Typically replies in a few minutes',
        mode: 'system',
      },
      welcomeMessage:
        "Having trouble with your assessment? Tell us what's happening and we'll help.",
    }),
  );
}

/**
 * Initialize the support widget. Same signature as the old placeholder so the
 * call site in LoadingScreen is unchanged.
 *
 * Respects the existing guard: no widget inside an iframe (SDK embed) or for a
 * whitelabelled (non-default-company) org — matching the Freshchat behaviour.
 */
export const initializeChatbot = (userData, isMobile = false) => {
  const inFrame = window.location !== window.parent.location;
  const [companyName] = setDocumentDetails();
  const isDefaultCompany = companyName.toLowerCase().includes(DEFAULT_COMPANY_NAME);
  if (inFrame || !isDefaultCompany) return;

  currentIsMobile = isMobile;
  currentUser = userData || storage.get.fcData?.() || null;

  render();

  // Preserve the app's contract: other code waits on this event to know support
  // chat is available.
  window.dispatchEvent(new Event(SUPPORT_CHAT_READY_EVENT));

  if (currentUser) storage.set.fcData?.(currentUser);
};

/**
 * Update the widget's user/metadata. Same signature as before; callers in
 * GameLoader pass `{ metadata: { gameName } }`, which merges into the live
 * metadata so the agent sees the current game.
 */
export const updateChatbotData = (userData) => {
  if (!userData) return;

  // GameLoader calls with { metadata: {...} }; a login flow may call with a user
  // object. Handle both without changing callers.
  if (userData.metadata && !userData.id) {
    currentMetadata = { ...currentMetadata, ...userData.metadata };
  } else {
    currentUser = { ...(currentUser || {}), ...userData };
    if (userData.metadata) currentMetadata = { ...currentMetadata, ...userData.metadata };
  }

  render();
};

/** Unmounts the widget, e.g. on logout. Optional — safe to call anytime. */
export const teardownChatbot = () => {
  if (root) {
    root.unmount();
    root = null;
  }
  currentUser = null;
  currentMetadata = {};
  const container = document.getElementById(CONTAINER_ID);
  if (container) container.remove();
};
